import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import type { Project } from '../../../services/projectManagement/projectsService';
import { pmCustomFieldsApiService } from '../../../services/projectManagement/pmCustomFieldsApiService';
import type { CustomField, CustomFieldInput, CustomFieldType } from '../../../services/projectManagement/customFieldsService';

interface ProjectCustomFieldsModalProps {
  organizationId: string;
  project: Project;
  onClose: () => void;
}

function toKebabCase(input: string) {
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

const FIELD_TYPES: { value: CustomFieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'multiselect', label: 'Multi-select' },
];

const ProjectCustomFieldsModal: React.FC<ProjectCustomFieldsModalProps> = ({ organizationId, project, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState<CustomField[]>([]);

  const [fieldName, setFieldName] = useState('');
  const [fieldKey, setFieldKey] = useState('');
  const [fieldType, setFieldType] = useState<CustomFieldType>('text');
  const [optionsText, setOptionsText] = useState('');
  const [required, setRequired] = useState(false);

  useEffect(() => {
    setFieldKey(toKebabCase(fieldName));
  }, [fieldName]);

  const fetchFields = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await pmCustomFieldsApiService.list({ organization_id: organizationId, project_id: project.id });
      if (error) {
        setFields([]);
      } else {
        setFields(data || []);
      }
    } catch {
      setFields([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId, project.id]);

  useEffect(() => {
    void fetchFields();
  }, [fetchFields]);

  const canHaveOptions = useMemo(() => fieldType === 'dropdown' || fieldType === 'multiselect', [fieldType]);

  const handleCreate = async () => {
    if (!fieldName.trim()) {
      toast.error('Field name is required');
      return;
    }
    if (!fieldKey.trim()) {
      toast.error('Field key is required');
      return;
    }

    const input: CustomFieldInput = {
      organization_id: organizationId,
      project_id: project.id,
      field_name: fieldName.trim(),
      field_key: fieldKey.trim(),
      field_type: fieldType,
      required,
      field_options: canHaveOptions
        ? { options: optionsText.split('\n').map(s => s.trim()).filter(Boolean) }
        : undefined,
    };

    const { error } = await pmCustomFieldsApiService.create(input);
    if (error) {
      toast.error(String(error));
      return;
    }

    toast.success('Custom field created');
    setFieldName('');
    setFieldKey('');
    setFieldType('text');
    setOptionsText('');
    setRequired(false);
    void fetchFields();
  };

  const handleDelete = async (field: CustomField) => {
    if (!window.confirm(`Delete field "${field.field_name}"?`)) return;

    const { error } = await pmCustomFieldsApiService.delete(field.id, organizationId);
    if (error) {
      toast.error(String(error));
      return;
    }

    toast.success('Field deleted');
    void fetchFields();
  };

  const moveField = async (fieldId: string, direction: 'up' | 'down') => {
    const idx = fields.findIndex(f => f.id === fieldId);
    if (idx === -1) return;

    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= fields.length) return;

    const next = [...fields];
    const tmp = next[idx];
    next[idx] = next[swapIdx];
    next[swapIdx] = tmp;

    setFields(next);

    const { error } = await pmCustomFieldsApiService.reorder({
      organization_id: organizationId,
      project_id: project.id,
      field_ids: next.map(f => f.id),
    });

    if (error) {
      toast.error(String(error));
      void fetchFields();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-3xl rounded-xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Custom Fields</h3>
            <div className="mt-1 text-sm text-gray-600">{project.name}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-gray-200 p-4">
            <div className="text-sm font-semibold text-gray-900 mb-3">Create Field</div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  value={fieldName}
                  onChange={(e) => setFieldName(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Key</label>
                <input
                  value={fieldKey}
                  onChange={(e) => setFieldKey(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={fieldType}
                  onChange={(e) => setFieldType(e.target.value as CustomFieldType)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  {FIELD_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              {canHaveOptions ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Options (one per line)</label>
                  <textarea
                    value={optionsText}
                    onChange={(e) => setOptionsText(e.target.value)}
                    rows={5}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              ) : null}

              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />
                Required
              </label>

              <button
                onClick={() => void handleCreate()}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
              >
                Create
              </button>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 p-4">
            <div className="text-sm font-semibold text-gray-900 mb-3">Fields</div>

            {loading ? (
              <div className="text-sm text-gray-500">Loading...</div>
            ) : fields.length === 0 ? (
              <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-600">
                No custom fields
              </div>
            ) : (
              <div className="space-y-2">
                {fields.map((f, idx) => (
                  <div key={f.id} className="flex items-center justify-between rounded-lg border border-gray-200 p-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate">{f.field_name}</div>
                      <div className="text-xs text-gray-500">{f.field_key} • {f.field_type}{f.required ? ' • required' : ''}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => void moveField(f.id, 'up')}
                        className="rounded bg-gray-100 px-2 py-1 text-sm hover:bg-gray-200"
                        disabled={idx === 0}
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => void moveField(f.id, 'down')}
                        className="rounded bg-gray-100 px-2 py-1 text-sm hover:bg-gray-200"
                        disabled={idx === fields.length - 1}
                      >
                        ↓
                      </button>
                      <button
                        onClick={() => void handleDelete(f)}
                        className="rounded bg-red-50 px-2 py-1 text-sm text-red-700 hover:bg-red-100"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold hover:bg-gray-50">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectCustomFieldsModal;
